import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Rocket, ExternalLink, UserPlus, CheckCircle2, Sparkles } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface OnboardingModalProps {
  isOpen: boolean;
  businessId: string;
}

type Step = "welcome" | "google-url" | "add-staff" | "success";

export function OnboardingModal({ isOpen, businessId }: OnboardingModalProps) {
  const [step, setStep] = useState<Step>("welcome");
  const [googleUrl, setGoogleUrl] = useState("");
  const [staffName, setStaffName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const updateBusinessMutation = useMutation({
    mutationFn: async (data: { googleReviewUrl?: string; onboardingCompleted?: boolean; onboardingSkipped?: boolean }) => {
      await apiRequest("PATCH", "/api/business", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business"] });
    },
  });

  const createStaffMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      await apiRequest("POST", "/api/staff", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/staff"] });
    },
  });

  const handleSkip = async () => {
    try {
      await updateBusinessMutation.mutateAsync({
        onboardingSkipped: true,
        onboardingCompleted: true
      });
      toast({
        title: "Skipped onboarding",
        description: "You can complete setup anytime from Settings.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to skip onboarding. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleGoogleUrlNext = async () => {
    if (!googleUrl.trim()) {
      // Allow skipping Google URL
      setStep("add-staff");
      return;
    }

    // Basic URL validation
    if (!googleUrl.includes("google.com") && !googleUrl.includes("g.page")) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid Google review link.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await updateBusinessMutation.mutateAsync({ googleReviewUrl: googleUrl });
      setStep("add-staff");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save Google URL. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStaffNext = async () => {
    if (!staffName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a staff member name.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createStaffMutation.mutateAsync({ name: staffName.trim() });
      await updateBusinessMutation.mutateAsync({ onboardingCompleted: true });
      setStep("success");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create staff member. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => {
    const steps = [
      { key: "welcome", number: 1 },
      { key: "google-url", number: 2 },
      { key: "add-staff", number: 3 },
    ];
    const currentStepIndex = steps.findIndex((s) => s.key === step);

    return (
      <div className="flex items-center justify-center gap-2 mb-6">
        {steps.map((s, index) => (
          <div key={s.key} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                index <= currentStepIndex
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {s.number}
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-12 h-0.5 mx-2 ${
                  index < currentStepIndex ? "bg-primary" : "bg-muted"
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[500px]" onPointerDownOutside={(e) => e.preventDefault()}>
        {step !== "success" && renderStepIndicator()}

        {step === "welcome" && (
          <>
            <DialogHeader>
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-primary/10 p-4">
                  <Rocket className="h-10 w-10 text-primary" />
                </div>
              </div>
              <DialogTitle className="text-center text-2xl">Welcome to ReviewKit!</DialogTitle>
              <DialogDescription className="text-center text-base">
                Let's get you set up in just 2 minutes. We'll help you add your Google review link and
                create your first team member.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 mt-4">
              <Button onClick={() => setStep("google-url")} size="lg" className="w-full">
                Let's Go <Sparkles className="ml-2 h-4 w-4" />
              </Button>
              <Button onClick={handleSkip} variant="ghost" className="w-full">
                Skip for now
              </Button>
            </div>
          </>
        )}

        {step === "google-url" && (
          <>
            <DialogHeader>
              <DialogTitle>Where should reviews go?</DialogTitle>
              <DialogDescription>
                Add your Google Business review link so customers can leave reviews.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="google-url">Google Review Link</Label>
                <Input
                  id="google-url"
                  placeholder="https://g.page/your-business/review"
                  value={googleUrl}
                  onChange={(e) => setGoogleUrl(e.target.value)}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />
                  <a
                    href="https://support.google.com/business/answer/7035772"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-primary"
                  >
                    How to find my Google review link
                  </a>
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setStep("welcome")} variant="outline" className="flex-1">
                  Back
                </Button>
                <Button onClick={handleGoogleUrlNext} disabled={isSubmitting} className="flex-1">
                  {isSubmitting ? "Saving..." : googleUrl.trim() ? "Continue" : "Skip"}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === "add-staff" && (
          <>
            <DialogHeader>
              <div className="flex justify-center mb-2">
                <UserPlus className="h-8 w-8 text-primary" />
              </div>
              <DialogTitle>Add your first team member</DialogTitle>
              <DialogDescription>
                Who will be collecting reviews? You can add more team members later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="staff-name">Team Member Name</Label>
                <Input
                  id="staff-name"
                  placeholder="e.g., John Smith"
                  value={staffName}
                  onChange={(e) => setStaffName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && staffName.trim()) {
                      handleStaffNext();
                    }
                  }}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setStep("google-url")} variant="outline" className="flex-1">
                  Back
                </Button>
                <Button onClick={handleStaffNext} disabled={isSubmitting || !staffName.trim()} className="flex-1">
                  {isSubmitting ? "Creating..." : "Complete Setup"}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <div className="flex flex-col items-center text-center py-6">
              <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-4 mb-4">
                <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
              </div>
              <DialogTitle className="text-2xl mb-2">You're all set!</DialogTitle>
              <DialogDescription className="text-base mb-6">
                Your account is ready to start collecting reviews.
              </DialogDescription>
              <div className="w-full space-y-2 mb-6 text-left bg-muted/50 p-4 rounded-lg">
                <p className="text-sm font-medium">Here's what you can do now:</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>Send email review requests to customers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>Generate QR codes for your team members</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>Track reviews and response rates</span>
                  </li>
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <Button
                  onClick={() => {
                    window.location.href = "/dashboard/qr-codes";
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  See QR Codes
                </Button>
                <Button
                  onClick={() => {
                    window.location.href = "/dashboard/send";
                  }}
                  className="flex-1"
                >
                  Send First Request
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
